import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { getRules, createRule, updateRule, deleteRule, toggleRule, getRooms, recalculatePrices } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Settings2, Zap, TrendingUp, Users, Swords, RefreshCw } from "lucide-react";

const ruleTypes = [
  { value: "occupancy", label: "Ocupación", icon: Users, description: "Ajusta precios según ocupación" },
  { value: "competitor", label: "Competencia", icon: Swords, description: "Mantén precios relativos a competidores" },
];

const conditions = [
  { value: ">", label: "Mayor que" },
  { value: "<", label: "Menor que" },
  { value: ">=", label: "Mayor o igual" },
  { value: "<=", label: "Menor o igual" },
  { value: "==", label: "Igual a" },
];

const RuleForm = ({ rule, rooms, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: rule?.name || "",
    description: rule?.description || "",
    rule_type: rule?.rule_type || "occupancy",
    condition: rule?.condition || ">",
    threshold: rule?.threshold || 80,
    adjustment_type: rule?.adjustment_type || "percentage",
    adjustment_value: rule?.adjustment_value || 10,
    room_type_id: rule?.room_type_id || "",
    priority: rule?.priority || 1,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      room_type_id: formData.room_type_id === "" ? null : formData.room_type_id,
    });
  };

  const getThresholdLabel = () => {
    if (formData.rule_type === "occupancy") return "% de Ocupación";
    if (formData.rule_type === "competitor") return "% por debajo de competencia";
    return "Valor";
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Nombre de la Regla</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ej: Alta Ocupación Fin de Semana"
          required
          data-testid="rule-name-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Descripción</Label>
        <Input
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Descripción de la regla..."
          data-testid="rule-description-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Tipo de Regla</Label>
          <Select
            value={formData.rule_type}
            onValueChange={(value) => setFormData({ ...formData, rule_type: value })}
          >
            <SelectTrigger data-testid="rule-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ruleTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Aplicar a</Label>
          <Select
            value={formData.room_type_id || "all"}
            onValueChange={(value) => setFormData({ ...formData, room_type_id: value === "all" ? "" : value })}
          >
            <SelectTrigger data-testid="room-select">
              <SelectValue placeholder="Todas las habitaciones" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las habitaciones</SelectItem>
              {rooms.map((room) => (
                <SelectItem key={room.id} value={room.id}>
                  {room.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {formData.rule_type === "occupancy" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Condición</Label>
            <Select
              value={formData.condition}
              onValueChange={(value) => setFormData({ ...formData, condition: value })}
            >
              <SelectTrigger data-testid="condition-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {conditions.map((cond) => (
                  <SelectItem key={cond.value} value={cond.value}>
                    {cond.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="threshold">{getThresholdLabel()}</Label>
            <Input
              id="threshold"
              type="number"
              value={formData.threshold}
              onChange={(e) => setFormData({ ...formData, threshold: parseFloat(e.target.value) })}
              data-testid="threshold-input"
            />
          </div>
        </div>
      )}

      {formData.rule_type === "competitor" && (
        <div className="space-y-2">
          <Label htmlFor="threshold">{getThresholdLabel()}</Label>
          <Input
            id="threshold"
            type="number"
            value={formData.threshold}
            onChange={(e) => setFormData({ ...formData, threshold: parseFloat(e.target.value) })}
            data-testid="threshold-input"
          />
          <p className="text-xs text-muted-foreground">
            El precio se ajustará para estar {formData.threshold}% por debajo del promedio de competidores
          </p>
        </div>
      )}

      {formData.rule_type === "occupancy" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Tipo de Ajuste</Label>
            <Select
              value={formData.adjustment_type}
              onValueChange={(value) => setFormData({ ...formData, adjustment_type: value })}
            >
              <SelectTrigger data-testid="adjustment-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                <SelectItem value="fixed">Cantidad Fija (€)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="adjustment">
              Ajuste {formData.adjustment_type === "percentage" ? "(%)" : "(€)"}
            </Label>
            <Input
              id="adjustment"
              type="number"
              value={formData.adjustment_value}
              onChange={(e) => setFormData({ ...formData, adjustment_value: parseFloat(e.target.value) })}
              data-testid="adjustment-input"
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="priority">Prioridad (1 = más alta)</Label>
        <Input
          id="priority"
          type="number"
          min="1"
          max="10"
          value={formData.priority}
          onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
          data-testid="priority-input"
        />
      </div>

      <div className="flex gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">
          Cancelar
        </Button>
        <Button type="submit" className="flex-1" data-testid="save-rule-btn">
          Guardar Regla
        </Button>
      </div>
    </form>
  );
};

const RuleCard = ({ rule, rooms, onToggle, onDelete }) => {
  const Icon = rule.rule_type === "occupancy" ? Users : Swords;
  const roomName = rule.room_type_id 
    ? rooms.find(r => r.id === rule.room_type_id)?.name || "Habitación específica"
    : "Todas las habitaciones";

  const getConditionText = () => {
    if (rule.rule_type === "occupancy") {
      const condText = {
        ">": "mayor que",
        "<": "menor que",
        ">=": "mayor o igual a",
        "<=": "menor o igual a",
        "==": "igual a",
      }[rule.condition] || rule.condition;
      
      const adjustText = rule.adjustment_value >= 0 
        ? `+${rule.adjustment_value}${rule.adjustment_type === "percentage" ? "%" : "€"}`
        : `${rule.adjustment_value}${rule.adjustment_type === "percentage" ? "%" : "€"}`;
      
      return `Si ocupación ${condText} ${rule.threshold}% → ${adjustText}`;
    }
    
    if (rule.rule_type === "competitor") {
      return `Mantener precio ${rule.threshold}% por debajo de competencia`;
    }
    
    return rule.description;
  };

  return (
    <Card className={`rule-card ${!rule.is_active ? "opacity-60" : ""}`} data-testid={`rule-card-${rule.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            rule.is_active ? "bg-primary/10" : "bg-muted"
          }`}>
            <Icon className={`w-5 h-5 ${rule.is_active ? "text-primary" : "text-muted-foreground"}`} />
          </div>
          <div>
            <h3 className="font-semibold">{rule.name}</h3>
            <p className="text-sm text-muted-foreground">{roomName}</p>
          </div>
        </div>
        <Switch
          checked={rule.is_active}
          onCheckedChange={() => onToggle(rule.id)}
          data-testid={`toggle-rule-${rule.id}`}
        />
      </div>
      
      <div className="mt-4 p-3 bg-muted/50 rounded-lg">
        <p className="text-sm font-mono">{getConditionText()}</p>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Prioridad: {rule.priority}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(rule.id)}
          className="text-destructive hover:text-destructive"
          data-testid={`delete-rule-${rule.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
};

const RevenueEngine = () => {
  const [rules, setRules] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  const fetchData = async () => {
    try {
      const [rulesRes, roomsRes] = await Promise.all([getRules(), getRooms()]);
      setRules(rulesRes.data);
      setRooms(roomsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      await createRule(data);
      toast.success("Regla creada");
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Error al crear regla");
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleRule(id);
      fetchData();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta regla?")) return;
    try {
      await deleteRule(id);
      toast.success("Regla eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar regla");
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await recalculatePrices();
      toast.success("Precios recalculados según las reglas activas");
    } catch (error) {
      toast.error("Error al recalcular precios");
    } finally {
      setRecalculating(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const activeRules = rules.filter(r => r.is_active).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="revenue-engine-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Motor de Precios</h1>
          <p className="text-muted-foreground">Configura las reglas de pricing automático</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleRecalculate} 
            disabled={recalculating}
            data-testid="recalculate-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${recalculating ? "animate-spin" : ""}`} />
            Aplicar Reglas
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-rule-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Regla
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Nueva Regla de Pricing</DialogTitle>
              </DialogHeader>
              <RuleForm
                rooms={rooms}
                onSave={handleSave}
                onClose={() => setDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <Zap className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <p className="font-semibold">{activeRules} reglas activas de {rules.length} totales</p>
              <p className="text-sm text-muted-foreground">
                Los precios se ajustan automáticamente cuando cambia la ocupación
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rule Types Explanation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {ruleTypes.map((type) => (
          <Card key={type.value}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <type.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">{type.label}</CardTitle>
                  <CardDescription>{type.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>

      {/* Rules List */}
      {rules.length === 0 ? (
        <Card className="p-12 text-center">
          <Settings2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No hay reglas configuradas</h3>
          <p className="text-muted-foreground mb-4">
            Crea reglas para automatizar los ajustes de precio según ocupación o competencia.
          </p>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Crear Primera Regla
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rules.map((rule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              rooms={rooms}
              onToggle={handleToggle}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RevenueEngine;
